import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import { Group } from 'lucide-react';
import React, { FC, useEffect, useMemo, useState } from 'react';

import { useHeroToast } from '../../../../hooks';
import { usePostOrganizationGroup } from '../../../../queries';

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (id: string, name: string) => void;
}

export const CreateGroupModal: FC<CreateGroupModalProps> = ({ isOpen, onClose, onCreated }) => {
  const { showSuccess, showError } = useHeroToast();
  const [name, setName] = useState('');
  const { mutate: createGroup, isPending } = usePostOrganizationGroup();

  useEffect(() => {
    if (!isOpen) setName('');
  }, [isOpen]);

  const trimmed = useMemo(() => name.trim(), [name]);
  const canSubmit = trimmed.length > 0 && !isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;
    createGroup(
      { name: trimmed },
      {
        onSuccess: (res) => {
          showSuccess(`Utworzono grupę "${res.name}"`);
          onCreated?.(res.id, res.name);
          onClose();
        },
        onError: (err) => {
          showError(err?.message || 'Nie udało się utworzyć grupy');
        },
      },
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => { if (!open && !isPending) onClose(); }}
      size="md"
      isDismissable={!isPending}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex items-center gap-2">
              <Group size={18} className="text-primary" />
              Utwórz grupę
            </ModalHeader>
            <ModalBody>
              <Input
                autoFocus
                label="Nazwa grupy"
                placeholder="np. Hotele premium"
                value={name}
                onValueChange={setName}
                isDisabled={isPending}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canSubmit) handleSubmit();
                }}
              />
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose} isDisabled={isPending}>
                Anuluj
              </Button>
              <Button
                color="primary"
                onPress={handleSubmit}
                isDisabled={!canSubmit}
                isLoading={isPending}
              >
                Utwórz
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
